import { IIntegerPrecisionService } from '@core/interfaces/integer-precision-service.interface';
import { TipCalculationFactory } from '@core/domain/tip';
import { CartStateFactory } from './cart-state.factory';
import { CartCalculationFactory } from './cart-calculation-factory.service';
import {
	calculateCartItemSubtotal,
	calculateCartTotal,
	calculateCouponDiscount,
	calculateDeliveryCharge,
	isCouponDiscountValid,
} from './util';
import {
	CartCalculationInput,
	CartCalculationResult,
	CartCalculationStrategy,
	CartSummary,
	CartTotalsEffectInput,
	CartTotalsPreparationContext,
	ValidCartTotalsPreparationContext,
} from './interfaces';
function hasValidTotalsContext(
	context: CartTotalsPreparationContext
): context is ValidCartTotalsPreparationContext {
	return context.isValid && context.cartItems !== null && context.workDay !== null;
}

export class CartCalculationProcessor {
	constructor(
		private readonly factory: CartCalculationFactory,
		private readonly stateFactory: CartStateFactory,
		private readonly tipFactory: TipCalculationFactory
	) {}

	calculateFromEffect(
		input: CartTotalsEffectInput,
		precisionService: IIntegerPrecisionService
	): CartCalculationResult | null {
		const initialContext: CartTotalsPreparationContext = {
			...input,
			isValid: false,
			eligibleCartItems: [],
			cartItemsWithSubtotalPrice: [],
		};
		const context = this.stateFactory.createTotalsPreparationRules().reduce<CartTotalsPreparationContext>(
			(currentContext, rule) => rule(currentContext),
			initialContext
		);

		if (!hasValidTotalsContext(context)) {
			return null;
		}

		return this.calculate({
			cartItems: context.cartItemsWithSubtotalPrice,
			coupon: context.coupon,
			deliveryZone: context.deliveryZone,
			orderType: context.orderType,
			tipDetail: context.tipDetail,
			precisionService,
			workDay: context.workDay,
		});
	}

	calculate(input: CartCalculationInput): CartCalculationResult {
		const regularSubtotal = input.cartItems.reduce(
			(subtotal, item) => subtotal + calculateCartItemSubtotal(item, 'price'),
			0n
		);
		const requestedCouponDiscount = calculateCouponDiscount(input.coupon);
		const isCouponInvalid = !isCouponDiscountValid(requestedCouponDiscount, regularSubtotal);
		const coupon = isCouponInvalid ? null : input.coupon;
		const couponDiscount = isCouponInvalid ? 0n : requestedCouponDiscount;
		const deliveryCharges = calculateDeliveryCharge(
			input.deliveryZone,
			regularSubtotal,
			input.precisionService,
			input.workDay
		);

		const regularSummary = this.calculateSummary(
			input,
			this.factory.create(false),
			coupon,
			couponDiscount,
			deliveryCharges
		);
		const cashSummary = this.calculateSummary(
			input,
			this.factory.create(true),
			coupon,
			couponDiscount,
			deliveryCharges
		);

		return {
			isCouponInvalid,
			cartTotals: {
				subtotal: regularSummary.subtotal,
				taxes: regularSummary.taxes,
				total: regularSummary.total,
				couponDiscount,
				tip: regularSummary.tip,
				deliveryCharges,
				totalCash: cashSummary.total,
			},
		};
	}

	private calculateSummary(
		input: CartCalculationInput,
		strategy: CartCalculationStrategy,
		coupon: CartCalculationInput['coupon'],
		couponDiscount: bigint,
		deliveryCharges: bigint
	): CartSummary {
		const totals = input.cartItems.reduce(
			(summary, item) => {
				const itemCouponDiscount = BigInt(
					coupon?.items?.find(couponItem => couponItem.id === item.idArticulo)?.valorCalculado ?? 0n
				);
				const itemTotals = strategy.calculateItem(
					item,
					itemCouponDiscount,
					input.precisionService,
					input.workDay
				);

				return {
					subtotal: summary.subtotal + itemTotals.subtotal,
					taxes: summary.taxes + itemTotals.taxes,
				};
			},
			{ subtotal: 0n, taxes: 0n }
		);
		const tip = input.tipDetail
			? this.tipFactory
					.create(input.tipDetail)
					.calculateAmount(
						input.tipDetail,
						totals.subtotal,
						input.precisionService,
						input.workDay
					)
			: 0n;

		return {
			...totals,
			tip,
			total: calculateCartTotal(
				totals.subtotal,
				totals.taxes,
				couponDiscount,
				tip,
				deliveryCharges,
				input.orderType
			),
		};
	}
}

export const cartCalculationProcessor = new CartCalculationProcessor(
	new CartCalculationFactory(),
	new CartStateFactory(),
	new TipCalculationFactory()
);
